import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { useGetTransactionItemDetail } from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { useGetBasicTransactionItemDetail } from '@/context/services/transaction_items/List/BasicTransactionItem.service';
import {
  ComponentType,
  TransactionItemsColumn,
} from '@/features/transaction_items/List/type';
import { TransactionItemBasicForm } from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemBasicForm';
import {
  TransactionItemGeneralForm,
  Props as TransactionItemGeneralFormProps,
} from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import { Loading } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, Suspense, memo, useEffect, useMemo } from 'react';
import styles from './DetailDrawer.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  selectedDetailId: string;
  setIsDetailLoading: (isLoading: boolean) => void;
  type: TransactionItemsColumn['type'];
} & TransactionItemGeneralFormProps;

type InnerProps = Omit<Props, 'type'>;

const castNumericValue = (value: string | number | undefined | null) => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }

  // Value will come from the API as a decimal string.
  // Integers will have a trailing ".0" that needs to be removed
  return String(value).split('.')[0];
};

const toNumberOrDefault = (
  value: string | number | undefined,
  defaultValue: number
) => {
  if (value === undefined) return defaultValue;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
};

const InnerGeneralDetailDrawer: FC<InnerProps> = memo(
  ({ selectedDetailId, setIsDetailLoading, ...props }) => {
    const { data } = useGetTransactionItemDetail(selectedDetailId);

    const castedDataToTransactionItemFormType = useMemo(() => {
      return {
        label: data?.label ?? '',
        is_visible: data?.is_visible ?? false,
        ...(props.isFlexibleValidationEnabled
          ? { is_required: data?.is_required }
          : {}),
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        component_type: data?.component_type as ComponentType,
        max_chars: toNumberOrDefault(data?.max_chars, 0),
        min_chars: toNumberOrDefault(data?.min_chars, 0),
        min_value: castNumericValue(data?.min_value),
        max_value: castNumericValue(data?.max_value),
        allow_decimal: !!data?.decimal_places,
        placeholder: data?.placeholder ?? '',
        tooltip_message: data?.tooltip_message ?? '',
        date_format_type: data?.date_format_type,
      };
    }, [data, props.isFlexibleValidationEnabled]);

    useEffect(() => {
      setIsDetailLoading(false);
    }, [setIsDetailLoading]);

    return (
      <TransactionItemGeneralForm
        {...props}
        selectedDetailId={selectedDetailId}
        defaultValues={castedDataToTransactionItemFormType}
      />
    );
  }
);
InnerGeneralDetailDrawer.displayName = 'InnerGeneralDetailDrawer';

const InnerBasicDetailDrawer: FC<InnerProps> = memo(
  ({ selectedDetailId, setIsDetailLoading, ...props }) => {
    const { data } = useGetBasicTransactionItemDetail(selectedDetailId);

    const itemDetails = data;

    useEffect(() => {
      setIsDetailLoading(false);
    }, [setIsDetailLoading]);

    return (
      <TransactionItemBasicForm
        {...props}
        itemId={itemDetails?.id}
        itemLabel={itemDetails?.label}
        itemPlaceholder={itemDetails?.placeholder}
        itemTooltip={itemDetails?.tooltip_message}
        itemIsRequired={itemDetails?.is_required}
      />
    );
  }
);
InnerBasicDetailDrawer.displayName = 'InnerBasicDetailDrawer';

const LoadingComponent: FC<InnerProps> = memo(({ setIsDetailLoading }) => {
  useEffect(() => {
    setIsDetailLoading(true);
  }, [setIsDetailLoading]);
  return (
    <div className={cx(styles['loading-container'])}>
      <Loading size='large' />
    </div>
  );
});
LoadingComponent.displayName = 'LoadingComponent';

export const DetailDrawer: FC<Props> = memo(({ type, ...props }) => {
  const isBasicItem = type === 'basic';

  return (
    <SuspenseErrorBoundary>
      <Suspense fallback={<LoadingComponent {...props} />}>
        {isBasicItem ? (
          <InnerBasicDetailDrawer {...props} />
        ) : (
          <InnerGeneralDetailDrawer {...props} />
        )}
      </Suspense>
    </SuspenseErrorBoundary>
  );
});
DetailDrawer.displayName = 'DetailDrawer';
